import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Button, Icon, Modal } from 'semantic-ui-react';

interface PropTypes {
  open: boolean;
  propertyId: number;
  address?: string;
  section?: string;
  block?: string;
  lot?: string;
  onClose: () => void;
}

const binNumbersCache: { [propertyId: number]: string[] } = {};
const binNumbersRequests: { [propertyId: number]: Promise<string[]> } = {};

const StyledModal = styled(Modal)`
  &&& {
    width: 460px !important;
    border-radius: 6px;
    overflow: hidden;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  background: #f7f7f9;
  border-bottom: 1px solid #e5e5ea;
`;

const Title = styled.div`
  font-family: Lato;
  font-weight: bold;
  font-size: 20px;
  color: #333333;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: #444444;
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: #ff5252;
  }
`;

const Body = styled.div`
  min-height: 140px;
  padding: 22px;
`;

const PropertyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
  padding: 12px 14px;
  border: 1px solid #ffc5c5;
  border-radius: 5px;
  background: #fff5f5;
`;

const AddressText = styled.div`
  font-family: Lato;
  font-size: 15px;
  font-weight: bold;
  color: #333333;
`;

const PropertyNumbers = styled.div`
  display: flex;
  gap: 18px;
  color: #4d4e4d;
  font-size: 14px;
`;

const SblRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333333;
  font-size: 14px;
`;

const PropertyNumberLabel = styled.span`
  font-weight: bold;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BinNumberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 8px 10px 8px 14px;
  border: 1px solid #e5e5ea;
  border-radius: 5px;
  background: #fbfbfc;
`;

const BinNumberText = styled.div`
  font-family: Lato;
  font-size: 15px;
  font-weight: bold;
  color: #333333;
`;

const BinNumberActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BinNumberLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  min-width: 44px;
  padding: 0 10px;
  border-radius: 5px;
  background: #ffffff;
  color: #444444;
  font-size: 12px;
  font-weight: bold;
  text-decoration: none;

  &:hover {
    color: #ff5252;
    background: #f7f7f9;
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  width: 30px;
  border: none;
  border-radius: 5px;
  background: #ffffff;
  color: #444444;
  cursor: pointer;

  &:hover {
    color: #ff5252;
    background: #f7f7f9;
  }
`;

const EmptyText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 96px;
  color: #666666;
  font-size: 14px;
`;

const ErrorText = styled.div`
  color: #c50e29;
  font-size: 14px;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 96px;
  gap: 12px;
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #e5e5ea;
  border-top-color: #ff5252;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const LoadingText = styled.div`
  color: #666666;
  font-size: 13px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 22px 22px;
`;

const getBinNumbers = (propertyId: number) => {
  if (binNumbersCache[propertyId]) {
    return Promise.resolve(binNumbersCache[propertyId]);
  }

  if (!binNumbersRequests[propertyId]) {
    binNumbersRequests[propertyId] = axios
      .get<string[]>(`/api/property/GetBinNumbers/${propertyId}`)
      .then(({ data }) => {
        const binNumbers = Array.isArray(data) ? data : [];
        binNumbersCache[propertyId] = binNumbers;
        return binNumbers;
      })
      .finally(() => {
        delete binNumbersRequests[propertyId];
      });
  }

  return binNumbersRequests[propertyId];
};

const getBisUrl = (binNumber: string) =>
  `https://a810-bisweb.nyc.gov/bisweb/PropertyProfileOverviewServlet?bin=${encodeURIComponent(
    binNumber
  )}&go4=+GO+&requestid=0`;

export const BinNumbersModal = (props: PropTypes) => {
  const [binNumbers, setBinNumbers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedBinNumber, setCopiedBinNumber] = useState('');
  const [copiedSbl, setCopiedSbl] = useState(false);
  const sbl = `${props.section ? props.section + '-' : ''}${props.block ? props.block + '-' : ''}${
    props.lot || ''
  }`;

  useEffect(() => {
    let mounted = true;

    if (!props.open) {
      return () => {
        mounted = false;
      };
    }

    setError('');
    setCopiedBinNumber('');
    setCopiedSbl(false);

    if (binNumbersCache[props.propertyId]) {
      setBinNumbers(binNumbersCache[props.propertyId]);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    getBinNumbers(props.propertyId)
      .then(data => {
        if (mounted) {
          setBinNumbers(data);
        }
      })
      .catch(() => {
        if (mounted) {
          setError('Unable to load BIN numbers.');
          setBinNumbers([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [props.open, props.propertyId]);

  const copyBinNumber = (binNumber: string) => {
    navigator.clipboard.writeText(binNumber.trim()).then(() => {
      setCopiedBinNumber(binNumber);
      setTimeout(() => setCopiedBinNumber(''), 1000);
    });
  };

  const copySbl = () => {
    navigator.clipboard.writeText(sbl.trim()).then(() => {
      setCopiedSbl(true);
      setTimeout(() => setCopiedSbl(false), 1000);
    });
  };

  return (
    <StyledModal open={props.open} onClose={props.onClose}>
      <ModalContent>
        <Header>
          <Title>BIN Numbers</Title>
          <CloseButton type="button" onClick={props.onClose} aria-label="Close BIN numbers">
            <Icon name="close" />
          </CloseButton>
        </Header>
        <Body>
          <PropertyInfo>
            {props.address && <AddressText>{props.address}</AddressText>}
            <PropertyNumbers>
              <div>
                <PropertyNumberLabel>Section: </PropertyNumberLabel>
                {props.section || ''}
              </div>
              <div>
                <PropertyNumberLabel>Block: </PropertyNumberLabel>
                {props.block || ''}
              </div>
              <div>
                <PropertyNumberLabel>Lot: </PropertyNumberLabel>
                {props.lot || ''}
              </div>
            </PropertyNumbers>
            {sbl && (
              <SblRow>
                <div>
                  <PropertyNumberLabel>SBL: </PropertyNumberLabel>
                  {sbl}
                </div>
                <CopyButton type="button" onClick={copySbl} title={`Copy ${sbl}`}>
                  <Icon name={copiedSbl ? 'check' : 'copy outline'} />
                </CopyButton>
              </SblRow>
            )}
          </PropertyInfo>
          {loading && (
            <LoadingContainer>
              <Spinner />
              <LoadingText>Looking up BIN numbers…</LoadingText>
            </LoadingContainer>
          )}
          {!loading && error && <ErrorText>{error}</ErrorText>}
          {!loading && !error && !binNumbers.length && <EmptyText>No BIN numbers found.</EmptyText>}
          {!loading && !error && !!binNumbers.length && (
            <List>
              {binNumbers.map((binNumber, index) => (
                <BinNumberRow key={`${binNumber}-${index}`}>
                  <BinNumberText>{binNumber}</BinNumberText>
                  <BinNumberActions>
                    <BinNumberLink href={getBisUrl(binNumber)} target="_blank" rel="noopener noreferrer">
                      BIS
                    </BinNumberLink>
                    <CopyButton
                      type="button"
                      onClick={() => copyBinNumber(binNumber)}
                      title={`Copy ${binNumber}`}
                    >
                      <Icon name={copiedBinNumber === binNumber ? 'check' : 'copy outline'} />
                    </CopyButton>
                  </BinNumberActions>
                </BinNumberRow>
              ))}
            </List>
          )}
        </Body>
        <Footer>
          <Button type="button" onClick={props.onClose}>
            Close
          </Button>
        </Footer>
      </ModalContent>
    </StyledModal>
  );
};
